import { auth } from '@/lib/firebase';
import { runtimeEnv } from '@/lib/runtime-env';
import type { TelegramEvent } from '@/lib/types/events';

const OUTBOX_KEY = 'telegram_notification_outbox';
const DIAG_KEY = 'notify_diag';
const WORKER_URL = 'https://poker-knights-3.eranhor.workers.dev/api/notifications/send';

interface OutboxItem {
  id: string;
  event: TelegramEvent;
  attempts: number;
  lastAttempt: number;
}

export interface DiagnosticEntry {
  timestamp: number;
  eventType: string;
  result: 'success' | 'error' | 'queued';
  error?: string;
  authTokenPresent: boolean;
  adminFallback: boolean;
  workerStatus?: number;
}

class NotificationService {
  private static retryTimer: number | null = null;

  private static recordDiagnostic(entry: DiagnosticEntry): void {
    try {
      const raw = sessionStorage.getItem(DIAG_KEY);
      const entries: DiagnosticEntry[] = raw ? JSON.parse(raw) : [];
      entries.push(entry);
      if (entries.length > 50) entries.splice(0, entries.length - 50);
      sessionStorage.setItem(DIAG_KEY, JSON.stringify(entries));
    } catch {
      // sessionStorage may be unavailable
    }
  }

  public static getDiagnostics(): DiagnosticEntry[] {
    try {
      const raw = sessionStorage.getItem(DIAG_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  }

  private static async getOutbox(): Promise<OutboxItem[]> {
    const data = localStorage.getItem(OUTBOX_KEY);
    return data ? JSON.parse(data) : [];
  }

  private static async saveOutbox(outbox: OutboxItem[]): Promise<void> {
    localStorage.setItem(OUTBOX_KEY, JSON.stringify(outbox));
  }

  // Event types that should NOT retry (fire-and-forget)
  private static readonly NO_RETRY_EVENTS = new Set(['SETTLEMENT_PUBLISHED', 'GAME_ENDED', 'TEST_SETTLEMENT']);

  public static async sendEvent(event: TelegramEvent): Promise<void> {
    // Fire-and-forget for settlement events: never retry, never duplicate
    if (this.NO_RETRY_EVENTS.has(event.type)) {
      // Await and surface errors so the UI can show them
      await this.dispatch(event);
      return;
    }

    const outbox = await this.getOutbox();
    const eventJson = JSON.stringify(event);
    const isDuplicate = outbox.some(item => JSON.stringify(item.event) === eventJson);
    if (!isDuplicate) {
      const newItem: OutboxItem = {
        id: crypto.randomUUID(),
        event,
        attempts: 0,
        lastAttempt: 0,
      };
      outbox.push(newItem);
      await this.saveOutbox(outbox);
    }
    
    // Trigger processing without awaiting it to avoid blocking the UI
    this.processOutbox().catch(console.error);
  }

  public static async sendTestMessage(message: string, mode: 'normal' | 'philosophy' = 'normal'): Promise<void> {
    await this.sendEvent({
      type: 'TEST_MESSAGE',
      payload: { message, mode },
    });
  }

  public static async processOutbox(): Promise<void> {
    // Use Web Locks API if available (atomic, cross-tab)
    if (typeof navigator !== 'undefined' && navigator.locks) {
      await navigator.locks.request('poker_knights_outbox', async () => {
        await this._processOutboxInternal();
      });
      return;
    }

    // Fallback: localStorage spin-lock
    const lockKey = 'poker_knights_outbox_lock';
    const myId = crypto.randomUUID();
    const start = Date.now();

    while (Date.now() - start < 5_000) {
      const current = localStorage.getItem(lockKey);
      if (!current) {
        localStorage.setItem(lockKey, JSON.stringify({ id: myId, time: Date.now() }));
        await new Promise(r => setTimeout(r, 50)); // let concurrent writes settle
        const verify = localStorage.getItem(lockKey);
        if (verify) {
          const { id } = JSON.parse(verify);
          if (id === myId) {
            try {
              await this._processOutboxInternal();
              return;
            } finally {
              localStorage.removeItem(lockKey);
            }
          }
        }
      } else {
        const { time } = JSON.parse(current);
        if (Date.now() - time > 30_000) {
          localStorage.removeItem(lockKey);
          continue;
        }
      }
      await new Promise(r => setTimeout(r, 100));
    }
  }

  private static async _processOutboxInternal(): Promise<void> {
    const outbox = await this.getOutbox();
      if (outbox.length === 0) return;

      const now = Date.now();
      const pending = outbox.filter(item => {
        // Retry every 30 seconds, with exponential backoff
        const waitTime = Math.min(1000 * 60 * 60, 1000 * Math.pow(2, item.attempts));
        return now - item.lastAttempt > waitTime;
      });

      for (const item of pending) {
        console.log('[NotificationService] Processing event:', item.event.type);
        try {
          await this.dispatch(item.event);
          
          // Remove from outbox on success
          const currentOutbox = await this.getOutbox();
          const filtered = currentOutbox.filter(i => i.id !== item.id);
          await this.saveOutbox(filtered);
        } catch (error) {
          console.error('[NotificationService] Outbox processing error:', error);
          
          const newAttempts = item.attempts + 1;
          
          if (newAttempts >= 3) {
            console.error(`[NotificationService] ⚠️ FAILED after ${newAttempts} attempts: ${item.event.type}. Check auth and network.`);
          }
          
          // Update attempt count and timestamp
          const currentOutbox = await this.getOutbox();
          const updated = currentOutbox.map(i => 
            i.id === item.id 
              ? { ...i, attempts: newAttempts, lastAttempt: now } 
              : i
          );
          await this.saveOutbox(updated);
        }
      }
  }
    
  private static async getAuthToken(): Promise<{ token?: string; adminFallback: boolean }> {
    // 1. Local dev admin bypass (dev/test environments only).
    //    Always active in non-production so emulator-signed tokens are never
    //    sent to the deployed worker, which cannot verify them.
    if (runtimeEnv.allowLocalAdminBypass) {
      const uid = auth.currentUser?.uid ?? 'localhost-uid';
      return { token: `devtoken-${uid}`, adminFallback: true };
    }

    // 2. Prefer real Firebase ID token (production only).
    try {
      const token = await auth.currentUser?.getIdToken();
      if (token) return { token, adminFallback: false };
    } catch {
      // getIdToken may fail — fall through to unauthenticated.
    }

    return { adminFallback: false };
  }

  private static async dispatch(event: TelegramEvent): Promise<void> {
    const { token, adminFallback } = await this.getAuthToken();

    if (!token) {
      const err = new Error('User not authenticated');
      this.recordDiagnostic({
        timestamp: Date.now(),
        eventType: event.type,
        result: 'error',
        error: err.message,
        authTokenPresent: false,
        adminFallback: false,
      });
      throw err;
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30_000);
    try {
      console.log('[NotificationService] Dispatching to:', WORKER_URL, 'Payload:', event);
      const response = await fetch(WORKER_URL, {
        method: 'POST',
        signal: controller.signal,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(event),
      });
      console.log('[NotificationService] Worker responded with status:', response.status);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMsg = errorData.error || `HTTP ${response.status}`;
        const err = new Error(errorMsg);
        this.recordDiagnostic({
          timestamp: Date.now(),
          eventType: event.type,
          result: 'error',
          error: errorMsg,
          authTokenPresent: !!token && !adminFallback,
          adminFallback,
          workerStatus: response.status,
        });
        throw err;
      }

      this.recordDiagnostic({
        timestamp: Date.now(),
        eventType: event.type,
        result: 'success',
        authTokenPresent: !!token && !adminFallback,
        adminFallback,
        workerStatus: response.status,
      });
      clearTimeout(timeoutId);
    } catch (error) {
      clearTimeout(timeoutId);
      const message = error instanceof Error ? error.message : String(error);
      this.recordDiagnostic({
        timestamp: Date.now(),
        eventType: event.type,
        result: 'error',
        error: message,
        authTokenPresent: !!token && !adminFallback,
        adminFallback,
      });
      throw error instanceof Error ? error : new Error(message);
    }
  }

  public static startRetryLoop(): void {
    if (this.retryTimer !== null) return; // Already running
    console.log('[NotificationService] Starting retry loop (every 30s)');
    this.retryTimer = window.setInterval(() => {
      this.processOutbox().catch(err => {
        console.error('[NotificationService] Retry loop error:', err);
      });
    }, 30_000);
  }
}

export default NotificationService;
